'use client'

import {useLayoutEffect, useState} from 'react'
import {breakpoints} from '../lib/utils'
import {resolveIsIOS} from '../lib/device-detection'
import type {ModalViewportState} from '@fitro/modal-routing'

interface MobileDetection {
    isMobile: boolean
    isTablet: boolean
    isDesktop: boolean
    isSmallMobile: boolean
    canHover: boolean
    isTouchDevice: boolean
    isIOS: boolean
    isAndroid: boolean
    viewportWidth: number
    isHydrated: boolean
}

function hasTouchScreen(): boolean {
    const msTouchPoints = (navigator as Navigator & { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0 || msTouchPoints > 0
}

/** The viewport as `useIsMobile` reports it, read once outside React (e.g. in a click handler). */
export function readModalViewportState(): ModalViewportState {
    const viewportWidth = window.innerWidth
    return {
        isHydrated: true,
        isMobile: viewportWidth < breakpoints.tablet,
        isTablet: viewportWidth >= breakpoints.tablet && viewportWidth < breakpoints.desktop,
        isTouchDevice: hasTouchScreen(),
        viewportWidth,
    }
}

export function useIsMobile(): MobileDetection {
    const [viewportWidth, setViewportWidth] = useState<number>(0)
    const [isHydrated, setIsHydrated] = useState(false)
    const [canHover, setCanHover] = useState(false)
    const [isTouchDevice, setIsTouchDevice] = useState(false)
    const [isIOS, setIsIOS] = useState(false)
    const [isAndroid, setIsAndroid] = useState(false)

    // Resolve viewport-dependent overlays before paint, including client-side remounts.
    useLayoutEffect(() => {
        setIsHydrated(true)
        setViewportWidth(window.innerWidth)

        const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)')
        setCanHover(hoverQuery.matches)

        setIsTouchDevice(hasTouchScreen())

        const userAgent = navigator.userAgent.toLowerCase()
        setIsIOS(resolveIsIOS(userAgent, navigator.maxTouchPoints ?? 0))
        setIsAndroid(/android/.test(userAgent))

        const handleResize = () => {
            setViewportWidth(window.innerWidth)
        }

        const handleHoverChange = (e: MediaQueryListEvent) => {
            setCanHover(e.matches)
        }

        window.addEventListener('resize', handleResize, {passive: true})
        hoverQuery.addEventListener('change', handleHoverChange)

        return () => {
            window.removeEventListener('resize', handleResize)
            hoverQuery.removeEventListener('change', handleHoverChange)
        }
    }, [])

    const effectiveWidth = isHydrated ? viewportWidth : 0

    return {
        isMobile: effectiveWidth < breakpoints.tablet,
        isTablet: effectiveWidth >= breakpoints.tablet && effectiveWidth < breakpoints.desktop,
        isDesktop: effectiveWidth >= breakpoints.desktop,
        isSmallMobile: effectiveWidth < breakpoints.sm,
        canHover: isHydrated ? canHover : false,
        isTouchDevice: isHydrated ? isTouchDevice : false,

        isIOS: isHydrated ? isIOS : false,
        isAndroid: isHydrated ? isAndroid : false,

        viewportWidth: effectiveWidth,
        isHydrated
    }
}
